'use client'

import {
  Form,
  FormGroupLabel,
  FormProvider,
  FormRadio,
  FormRadioGroup,
  FormSubmit,
  useFormContext,
  useFormStore,
} from '@ariakit/react'
import capitalize from 'lodash-es/capitalize'
import styled from 'styled-components'

import {
  Button,
  ColorSwatch,
  ColorSwatchWrapper,
  QuickBuy as QuickBuyIcon,
  ResponsiveImage,
  SizeSelectorSelectorButton,
  Skeleton,
  Spacer,
  TypeStyle,
  TypeStyleHeadlineExtraSmall,
  focusOutline,
  fromMd,
  untilMd,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import {
  QuickBuyProductFieldsFragment,
  QuickBuyVariantFieldsFragment,
  QuickBuyVariantGroupFieldsFragment,
} from '../../../__generated__/graphql/catalog/graphql'
import { CartErrorMessage } from '../../../components/atoms/CartErrorMessage/CartErrorMessage'
import {
  WaitlistModalProvider,
  useWaitlistModal,
} from '../../../components/pages/products/ProductPage/WaitlistModal'
import { trackEvent } from '../../../lib/analytics'
import { getDollarsAndCentsForAmount } from '../../../lib/utils/currency'
import { useNextQuery } from '../../_hooks/useNextQuery'
import { useCart, useCartDialog } from '../../_providers/CartProvider.client'
import { useLocalization } from '../../_providers/LocalizationProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { groupColorOptionsByColorGroup } from '../../_utils/groupColorOptionsByColorGroup'
import { FormSubmitContent } from '../form/FormSubmit'
import { NextLink } from '../navigation/NextLink'
import {
  PricingRange,
  derivePricingForVariant,
  derivePricingForVariantGroup,
} from '../pricing/PricingRange.client'
import { ProductTileDetails, ProductTilePrice, ProductTileTitle } from '../tiles/ProductTile.client'

import { NextDialogProvider, NextDialogTrigger, useNextDialogContext } from './NextDialog.client'
import { NextDrawerDialog } from './NextDrawerDialog.client'

export const quickBuyProductFields = gql(`
  fragment QuickBuyProductFields on ProductCatalogProduct {
    __typename
    id
    handle
    options(color: $selectedColor, fit: $selectedFit, size: $selectedSize) {
      colors {
        color {
          id
          hexCodes
          displayValue
          swatchImage {
            url
          }
          group {
            id
            displayValue
          }
        }
        outOfStock
      }
      fits {
        fit {
          id
          displayValue
        }
        outOfStock
      }
      sizes {
        size {
          id
          displayValue
        }
        outOfStock
      }
    }
  }
`)

export const quickBuyVariantGroupFields = gql(`
  fragment QuickBuyVariantGroupFields on ProductCatalogVariantGroup {
    __typename
    id
    title
    gender
    exclusive
    availability
    finalSale
    siteWidePromoEligible
    defaultImages(first: 1) {
      url
    }
    defaultVariant {
      urlPath
    }
    primaryCategory {
      id
    }
    priceRange {
      min {
        amount
        currencyCode
      }
      max {
        amount
        currencyCode
      }
    }
    compareAtPriceRange {
      min {
        amount
      }
      max {
        amount
      }
    }
    color {
      group {
        id
        displayValue
      }
      hexCodes
      displayValue
      id
    }
    variant(fit: $selectedFit, size: $selectedSize) @include(if: $sizeAndFitSelected) {
      ...QuickBuyVariantFields
    }
  }
`)

export const quickBuyVariantFields = gql(`
  fragment QuickBuyVariantFields on ProductCatalogVariant {
    __typename
    id
    sku
    availability
    shopifyId
    shopifyProductId
    urlPath
    images(first: 1) {
      url
    }
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
  }
`)

const getQuickBuyProductInfoQuery = gql(`
  query GetQuickBuyProductInfo(
      $collectionHandle: String, 
      $catalogProductHandle: String!, 
      $selectedColor: String!, 
      $selectedFit: String!, 
      $selectedSize: String!, 
      $useCollectionScope: Boolean!, 
      $sizeAndFitSelected: Boolean!
  ) {
    productCatalogProduct(handle: $catalogProductHandle) @skip(if: $useCollectionScope) {
      ...QuickBuyProductFields
      variantGroup(color: $selectedColor) {
        ...QuickBuyVariantGroupFields
      }
    }  
    productCatalogCollection(handle: $collectionHandle) @include(if: $useCollectionScope) {
      item(productHandle: $catalogProductHandle, color: $selectedColor) {
        variantGroup {
          ...QuickBuyVariantGroupFields
          product {
            ...QuickBuyProductFields
          }
        }
      }
    }
  }
`)

type QuickBuyDialogProps = QuickBuyFormProps

export const QuickBuyDialog: React.FC<QuickBuyDialogProps> = props => {
  const translations = useTranslation()
  return (
    <NextDialogProvider>
      <NextDialogTrigger
        render={
          <QuickBuyButton
            type='button'
            aria-label={translations.quickBuy.modal.headline}
            {...trackEvent({
              action: 'click',
              category: `quick buy`,
              label: 'quick-buy',
            })}
          >
            <QuickBuyIcon />
          </QuickBuyButton>
        }
      />
      <NextDrawerDialog
        unmountOnHide={true}
        variant='bottom'
        closeText={translations.magnolia.general.close}
      >
        <QuickBuyContent>
          <TitleBar>
            <TypeStyleHeadlineExtraSmall textTransform='uppercase'>
              {translations.quickBuy.modal.headline}
            </TypeStyleHeadlineExtraSmall>
          </TitleBar>
          <FormProvider>
            <QuickBuyForm {...props} />
          </FormProvider>
        </QuickBuyContent>
      </NextDrawerDialog>
    </NextDialogProvider>
  )
}

type QuickBuyFormProps = {
  productGroupHandle: string
  collectionHandle: string | null | undefined
  initialColor: string
  initialFit: string | null | undefined
  initialSize: string | null | undefined
}

const QuickBuyForm: React.FC<QuickBuyFormProps> = ({
  initialColor,
  initialFit,
  initialSize,
  productGroupHandle,
  collectionHandle,
}) => {
  const translations = useTranslation()
  const defaultValues = { color: initialColor, fit: initialFit, size: initialSize }
  const form = useFormStore({
    defaultValues: defaultValues,
  })

  const formFieldColor = form.useValue<string>('color') ?? ''
  const selectedColor = formFieldColor.length > 0 ? formFieldColor : initialColor

  const formFieldFit = form.useValue<string>('fit') ?? ''
  const selectedFit = formFieldFit.length > 0 ? formFieldFit : undefined

  const formFieldSize = form.useValue<string>('size') ?? ''
  const selectedSize = formFieldSize.length > 0 ? formFieldSize : undefined

  const sizeAndFitSelected = !!selectedFit && !!selectedSize

  const { data, previousData, status } = useNextQuery(getQuickBuyProductInfoQuery, {
    variables: {
      catalogProductHandle: productGroupHandle,
      collectionHandle,
      selectedColor,
      selectedFit: selectedFit ?? '',
      selectedSize: selectedSize ?? '',
      sizeAndFitSelected,
      useCollectionScope: !!collectionHandle,
    },
  })

  const product =
    data?.productCatalogCollection?.item?.variantGroup.product ??
    previousData?.productCatalogCollection?.item?.variantGroup.product ??
    data?.productCatalogProduct ??
    previousData?.productCatalogProduct
  const variantGroup =
    data?.productCatalogCollection?.item?.variantGroup ??
    previousData?.productCatalogCollection?.item?.variantGroup ??
    data?.productCatalogProduct?.variantGroup ??
    previousData?.productCatalogProduct?.variantGroup
  const selectedVariant = variantGroup?.variant

  if (!product || !variantGroup) {
    if (status === 'idle' || status === 'pending') {
      return <QuickbuyFormSkeleton />
    } else {
      // TODO: Create and render a QuickBuyFormNotFound component
      return null
    }
  }

  if (status === 'rejected') {
    // TODO: Create and render a QuickBuyFormNotFound component
    return null
  }

  const pdpUrl = selectedVariant?.urlPath ?? variantGroup.defaultVariant.urlPath

  const selectedImage = selectedVariant?.images[0]?.url ?? variantGroup.defaultImages?.[0]?.url
  const pricingRange = selectedVariant
    ? derivePricingForVariant(selectedVariant)
    : derivePricingForVariantGroup(variantGroup)

  const selectedColorOption = product.options.colors.find(
    colorOption => colorOption.color.id === selectedColor
  )
  const selectedColorDisplayValue = selectedColorOption?.color.displayValue ?? selectedColor

  const selectedFitDisplayValue =
    product.options.fits.find(fitOption => fitOption.fit.id === selectedFit)?.fit.displayValue ??
    selectedFit

  const selectedSizeDisplayValue =
    product.options.sizes.find(sizeOption => sizeOption.size.id === selectedSize)?.size
      .displayValue ?? selectedSize

  const colorGroups = groupColorOptionsByColorGroup(product.options.colors)

  return (
    <WaitlistModalProvider
      colorDisplayValue={selectedColorDisplayValue}
      variantShopifyId={selectedVariant?.shopifyId}
      productTitle={variantGroup.title}
    >
      <ProductPreview>
        {selectedImage && (
          <NextLink
            href={pdpUrl}
            {...trackEvent({
              action: 'click',
              category: 'Quick Buy',
              label: 'Click Product',
              value: productGroupHandle,
            })}
          >
            <ProductImage
              srcs={{
                sm: selectedImage,
                md: selectedImage,
              }}
              src={selectedImage}
              widths={{ unit: 'px', sm: 100, md: 100 }}
              alt={variantGroup.title}
              loading='default'
              aspectRatios={{ sm: 1, md: 1 }}
            />
          </NextLink>
        )}
        <ProductTileDetails untilMdLeftPadding={false} noMargin>
          <NextLink
            href={pdpUrl}
            {...trackEvent({
              action: 'click',
              category: 'Quick Buy',
              label: 'Click Product',
              value: productGroupHandle,
            })}
          >
            <ProductTileTitle>{variantGroup.title}</ProductTileTitle>
          </NextLink>
          {pricingRange ? (
            <ProductTilePrice>
              <PricingRange upToPercentageOff={null} percentageOff={null} {...pricingRange} />
            </ProductTilePrice>
          ) : null}
        </ProductTileDetails>
      </ProductPreview>
      <Form store={form} resetOnSubmit={false}>
        <QuickBuyFormFields>
          <ColorGroupRadioGroup>
            {colorGroups.map(colorGroup => (
              <div key={colorGroup.group.id}>
                <StyledFormGroupLabel>
                  {colorGroup.group.displayValue}
                  {selectedColorOption?.color.group?.id === colorGroup.group.id ? (
                    <FormValue>{selectedColorDisplayValue}</FormValue>
                  ) : null}
                </StyledFormGroupLabel>

                <OptionsRow>
                  {colorGroup.colorOptions.map(colorOption => {
                    const disabled = colorOption.outOfStock
                    const isSelected = selectedColor === colorOption.color.id

                    return (
                      <FormRadio
                        key={colorOption.color.id}
                        name='color'
                        value={colorOption.color.id}
                        render={htmlProps => (
                          <label
                            htmlFor={htmlProps.id}
                            {...trackEvent({
                              action: 'click',
                              category: 'Quick Buy',
                              label: 'Color Swatch',
                              value: colorOption.color.id,
                            })}
                          >
                            <InvisibleInput
                              {...htmlProps}
                              aria-label={colorOption.color.displayValue}
                            />
                            <ColorSwatch
                              hexCodes={colorOption.color.hexCodes ?? []}
                              swatchUrl={colorOption.color.swatchImage?.url}
                              selected={isSelected}
                              unavailable={disabled}
                              aria-label={colorOption.color.displayValue}
                              aria-hidden='true'
                              size='medium'
                            />
                          </label>
                        )}
                      />
                    )
                  })}
                </OptionsRow>
              </div>
            ))}
          </ColorGroupRadioGroup>

          <FormRadioGroup>
            <StyledFormGroupLabel>
              {translations.quickBuy.modal.size} <FormValue>{selectedSizeDisplayValue}</FormValue>
            </StyledFormGroupLabel>
            <OptionsRow>
              {product.options.sizes.map(sizeOption => {
                const size = sizeOption.size
                const disabled = sizeOption.outOfStock
                return (
                  <FormRadio
                    name={'size'}
                    key={size.id}
                    value={size.id}
                    render={props => {
                      return (
                        <label
                          htmlFor={props.id}
                          {...trackEvent({
                            action: 'click',
                            category: 'Quick Buy',
                            label: 'Size',
                            value: size.id,
                          })}
                        >
                          <InvisibleInput {...props} aria-label={size.displayValue} />
                          <Selector
                            aria-hidden={true}
                            aria-label={size.displayValue}
                            isSelected={selectedSize === size.id}
                            soldOut={disabled}
                            as='div'
                          >
                            {size.displayValue}
                          </Selector>
                        </label>
                      )
                    }}
                  />
                )
              })}
            </OptionsRow>
          </FormRadioGroup>

          <FormRadioGroup>
            <StyledFormGroupLabel>
              {translations.magnolia.reviewsPage.filters.filter.Fit.title}
              <FormValue>{capitalize(selectedFitDisplayValue)}</FormValue>
            </StyledFormGroupLabel>
            <OptionsRow>
              {product.options.fits.map(fitOption => {
                const fit = fitOption.fit
                const disabled = fitOption.outOfStock
                return (
                  <FormRadio
                    name={'fit'}
                    key={fit.id}
                    value={fit.id}
                    render={props => {
                      return (
                        <label
                          htmlFor={props.id}
                          {...trackEvent({
                            action: 'click',
                            category: 'Quick Buy',
                            label: 'Fit',
                            value: fit.id,
                          })}
                        >
                          <InvisibleInput {...props} aria-label={fit.displayValue} />
                          <Selector
                            aria-hidden={true}
                            aria-label={fit.displayValue}
                            isSelected={selectedFit === fit.id}
                            soldOut={disabled}
                            as='div'
                          >
                            {capitalize(fit.displayValue)}
                          </Selector>
                        </label>
                      )
                    }}
                  />
                )
              })}
            </OptionsRow>
          </FormRadioGroup>
        </QuickBuyFormFields>

        <QuickBuyFormSubmit
          product={product}
          selectedVariant={selectedVariant}
          variantGroup={variantGroup}
          isLoadingNextSelectedVariant={status === 'pending'}
        />
      </Form>
    </WaitlistModalProvider>
  )
}

type QuickBuyFormSubmitProps = {
  product: QuickBuyProductFieldsFragment
  variantGroup: QuickBuyVariantGroupFieldsFragment
  selectedVariant: QuickBuyVariantFieldsFragment | undefined | null
  isLoadingNextSelectedVariant: boolean
}

const QuickBuyFormSubmit = ({
  product,
  selectedVariant,
  variantGroup,
  isLoadingNextSelectedVariant,
}: QuickBuyFormSubmitProps) => {
  const translations = useTranslation()
  const { currency, locale } = useLocalization()
  const quickBuyDialogContext = useNextDialogContext()
  const { addItems, errorMessage } = useCart()
  const { open: openCartDialog } = useCartDialog()
  const { openWaitlistModal } = useWaitlistModal()
  const formContext = useFormContext()

  const variantAvailabilityIsSupported =
    selectedVariant &&
    (selectedVariant.availability === 'AVAILABLE' ||
      selectedVariant.availability === 'WAIT_LISTING')

  formContext?.useSubmit(() => {
    if (selectedVariant) {
      if (selectedVariant.availability === 'AVAILABLE') {
        addItems([
          {
            quantity: 1,
            finalSale: variantGroup.finalSale,
            swPromoEligible: variantGroup.siteWidePromoEligible,
            item: {
              sku: selectedVariant.sku,
              shopifyId: selectedVariant.shopifyId,
              externalParentId: selectedVariant.shopifyProductId,
              currencyType: selectedVariant.price.currencyCode,
              category: variantGroup.primaryCategory.id,
              handle: `${product.handle}-${variantGroup.color?.id}`,
              price: getDollarsAndCentsForAmount({
                currency,
                locale,
                value: selectedVariant.compareAtPrice?.amount ?? selectedVariant.price.amount,
                unit: 'dollars',
              }).cents,
              discountPrice: selectedVariant.compareAtPrice?.amount
                ? getDollarsAndCentsForAmount({
                    currency,
                    locale,
                    value: selectedVariant.price.amount,
                    unit: 'dollars',
                  }).cents
                : null,
              product: {
                shopifyId: selectedVariant.shopifyProductId,
              },
            },
          },
        ])
        if (!errorMessage) {
          quickBuyDialogContext?.hide()
          openCartDialog()
        }
      } else if (selectedVariant.availability === 'WAIT_LISTING') {
        openWaitlistModal()
      }
    }
  })

  return (
    <QuickBuyDialogFooter>
      <FormSubmit
        render={htmlProps => (
          <>
            <Button
              disabled={isLoadingNextSelectedVariant || !variantAvailabilityIsSupported}
              {...htmlProps}
              {...trackEvent({
                action: 'click',
                category: 'Quick Buy',
                label: 'Add to Bag',
              })}
            >
              <FormSubmitContent
                isSubmitting={isLoadingNextSelectedVariant}
                submittingLabel={translations.magnolia.general.loading}
              >
                {!selectedVariant
                  ? translations.detailsSection.addToBagCTA
                  : selectedVariant.availability === 'WAIT_LISTING'
                  ? translations.detailsSection.waitlistAddToBagCTA
                  : selectedVariant.availability === 'AVAILABLE'
                  ? translations.detailsSection.addToBagCTA
                  : translations.detailsSection.soldOutAddToBagCTA}
              </FormSubmitContent>
            </Button>
            {errorMessage && (
              <ErrorMessageContainer>
                <CartErrorMessage errorMessage={errorMessage} />
              </ErrorMessageContainer>
            )}
          </>
        )}
      />
    </QuickBuyDialogFooter>
  )
}

export const QuickbuyFormSkeleton: React.FC = () => (
  <div>
    <ProductPreview>
      <Skeleton height={'80px'} width={'80px'}></Skeleton>
      <ProductTileDetails untilMdLeftPadding={false} noMargin>
        <Skeleton height={'20px'} width={'100px'}></Skeleton>
        <Skeleton height={'20px'} width={'100px'}></Skeleton>
      </ProductTileDetails>
    </ProductPreview>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'50px'} width={'100%'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
  </div>
)

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${o => o.theme.spacing(4)};
`

const ProductPreview = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${o => o.theme.spacing(4)};
  gap: ${o => o.theme.spacing(2)};
`

const QuickBuyButton = styled.button`
  position: absolute;
  bottom: ${o => o.theme.spacing(2)};
  right: ${o => o.theme.spacing(2)};
`

const QuickBuyContent = styled.div`
  text-align: left;
  padding: ${o => o.theme.spacing(6, 2, 0, 2)};
  max-width: 812px;
  ${fromMd} {
    min-width: 812px;
  }
  margin-left: auto;
  margin-right: auto;
`

const OptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${o => o.theme.spacing(2)};
  width: 100%;
`

const ProductImage = styled(ResponsiveImage)`
  width: 80px;
`

const QuickBuyFormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(6)};
`

const QuickBuyDialogFooter = styled.div`
  padding-top: ${o => o.theme.spacing(6)};
  padding-bottom: ${o => o.theme.spacing(6)};
  width: 100%;
  display: flex;
  align-items: stretch;
  flex-direction: column;
`

const ColorGroupRadioGroup = styled(FormRadioGroup)`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
`

const Selector = styled(SizeSelectorSelectorButton)`
  min-width: 30px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const InvisibleInput = styled.input`
  height: 0;
  width: 0;
  opacity: 0;

  &:focus-visible + ${ColorSwatchWrapper}, &:focus-visible + ${Selector} {
    ${focusOutline};
    outline-style: solid;
  }
  margin: 0;
  position: absolute;
`

const StyledFormGroupLabel = styled(FormGroupLabel)`
  display: flex;
  ${TypeStyle.css.headlineMicro};
  gap: ${o => o.theme.spacing(1)};
  justify-self: flex-start;
  font-weight: 700;
  text-transform: uppercase;
  ${o => o.theme.typography.effect.uppercase};
  margin-bottom: ${o => o.theme.spacing(2)};
`

const FormValue = styled.span`
  color: ${o => o.theme.color.text.tertiary.on.background};
  ${TypeStyle.css.bodyMicro};
  text-transform: none;
`

export const ErrorMessageContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};

  ${untilMd} {
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`
